import { useState, useEffect, useMemo } from 'react'
import { useAppStore } from '@store/appStore'
import { useDatasets } from '@hooks/useDatasets'
import { useImagePreloader } from '@hooks/useImagePreloader'
import { useFullscreen } from '@/hooks/useFullscreen'
import { useNavigation } from '@/hooks/useNavigation'
import { usePlayback } from '@/hooks/usePlayback'
import { NewModeViewer } from './NewModeViewer'
import { MeshViewer } from '@components/MeshViewer/MeshViewer'
import ThreeDViewerDataSync from '@/components/ThreeDViewer'
import { MobileControlsOverlay } from '@/components/MobileControlsOverlay/MobileControlsOverlay'
import { SectionsDrawer } from '@/components/SectionsDrawer/SectionsDrawer'
import { LoadingProgressBar } from '@/components/LoadingProgressBar/LoadingProgressBar'
import { NavigationMenu } from '@/components/NavigationMenu'
import { SettingsPanel } from '@/components/SettingsPanel'
import { resolveUrl } from '@/config'
import { WorldSpaceDebug } from '@/components/WorldSpaceDebug'
import { MobileHistoryButtons } from '@/components/NavigationHistory'
import { shouldDismissOverlays } from '@/utils/overlayUtils'
import './GestureViewer.css'

interface GestureViewerProps {
  shopId: string
}

function GestureViewer({ shopId }: GestureViewerProps) {
  // Fix: Use separate selectors to avoid creating new objects on every render
  const cameras = useAppStore((state) => state.cameras)
  const sections = useAppStore((state) => state.sections)
  const isLoadingData = useAppStore((state) => state.isLoadingData)
  const loadingProgress = useAppStore((state) => state.loadingProgress)
  const canUseTapIndicator = useAppStore((state) => state.canUseTapIndicator)
  
  const loadingStage = useAppStore((state) => state.loadingStage)
  const dataError = useAppStore((state) => state.dataError)
  const currentCameraIndex = useAppStore((state) => state.currentCameraIndex)
  const showDebugOverlay = useAppStore((state) => state.showDebugOverlay)
  const showMeshViewer = useAppStore((state) => state.showMeshViewer)
  const currentDatasetId = useAppStore((state) => state.currentDatasetId)
  const loadDataset = useAppStore((state) => state.loadDataset)
  const is3DMode = useAppStore((state) => state.is3DMode)
  
  // Menu state
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isBalloonOpen, setIsBalloonOpen] = useState(false)
  
  // World space debug view state
  const [showWorldDebug, setShowWorldDebug] = useState(false)
  
  // Load datasets manifest (only loads manifest, doesn't auto-load dataset)
  useDatasets()
  
  // Initialize navigation history recording and keyboard shortcuts
  useNavigation()

  // Initialize playback functionality
  usePlayback()
  
  // Load the specific dataset when shopId changes
  // Get dataset base path for warm-start frame
  const availableDatasets = useAppStore(state => state.availableDatasets)
  const warmStartUrl = useMemo(() => {
    if (!shopId) return ''
    // Get base path from dataset configuration to support folder organization (e.g., NewShops/)
    const dataset = availableDatasets.find(d => d.id === shopId)
    const datasetBasePath = dataset?.paths?.metadata?.replace('/shop_metadata.json', '') || `/api/scan-data/${shopId}`
    return resolveUrl(`${datasetBasePath}/section_0/frames/frame_0000.jpg?shop=${shopId}`)
  }, [shopId, availableDatasets])

  useEffect(() => {
    console.log('🎬 [GestureViewer] useEffect triggered:', { shopId, currentDatasetId, isLoadingData })
    if (shopId && shopId !== currentDatasetId) {
      console.log('🎬 [GestureViewer] Starting dataset load for:', shopId)
      
      // Extract URL parameters for target section/frame
      const urlParams = new URLSearchParams(window.location.search)
      const targetSection = urlParams.get('section') ? parseInt(urlParams.get('section')!, 10) : undefined
      const targetFrame = urlParams.get('frame') ? parseInt(urlParams.get('frame')!, 10) : undefined
      
      if (targetSection !== undefined || targetFrame !== undefined) {
        console.log('🔗 [GestureViewer] URL navigation detected:', { targetSection, targetFrame })
        
        // Clear params immediately after reading to prevent double-processing
        // They'll be restored after navigation completes (by loadDataset)
        // NOTE: Actually, we'll clear them AFTER navigation succeeds in loadDataset
        // Don't clear here to support browser refresh during loading
      }
      
      // Set warm-start frame to show immediately while manifest loads
      useAppStore.getState().setWarmStartFrame(warmStartUrl)
      
      // Set loading state immediately when starting to load
      useAppStore.getState().setLoadingData(true)

      // Call loadDataset with URL parameters (it handles errors internally)
      loadDataset(shopId, { 
        section: targetSection, 
        frame: targetFrame,
        skipAnimation: targetFrame !== undefined // Skip animation if specific frame requested
      })
    } else {
      console.log('🎬 [GestureViewer] Skipping load:', { shopId, currentDatasetId, condition: shopId && shopId !== currentDatasetId })
    }
  }, [shopId, currentDatasetId])
  
  // Enable image preloading (now uses current dataset from store)
  useImagePreloader(currentDatasetId || shopId)
  
  const currentCamera = cameras[currentCameraIndex]
  
  // Call hooks BEFORE any early returns
  const { isFullscreen, isSupported: isFullscreenSupported, toggleFullscreen } = useFullscreen()
  
  // Define handlers before useEffect
  const handleBackClick = () => {
    // Close any open panels first
    setIsMenuOpen(false)
    setIsSettingsOpen(false)
    
    // Save current shop to lastSelectedShop so ShopSelector can highlight it
    const currentShopId = useAppStore.getState().currentShopId
    if (currentShopId) {
      useAppStore.getState().setLastSelectedShop(currentShopId)
    }
    
    // Navigate back to shop list by clearing URL and resetting state
    window.history.replaceState({}, '', window.location.pathname)
    useAppStore.getState().setCurrentShop('')
    // Dispatch custom event to trigger App re-render
    window.dispatchEvent(new CustomEvent('urlChanged'))
  }
  
  const handleMenuClick = () => {
    setIsMenuOpen(!isMenuOpen)
  }
  
  const handleMenuClose = () => {
    console.log('🎬 [GestureViewer] Menu close called')
    setIsMenuOpen(false)
  }
  
  const handleSettingsOpen = () => {
    setIsMenuOpen(false)
    setIsSettingsOpen(true)
  }
  
  const handleSettingsClose = () => {
    setIsSettingsOpen(false)
  }
  
  // Handle tap detection for button visibility
  useEffect(() => {
    const TAP_THRESHOLD = 10;
    let touchStartPos: { x: number; y: number } | null = null;
    let touchStartTime: number = 0;
    let lastTouchTime: number = 0;
    let longPressTimer: NodeJS.Timeout | null = null;

    const handleTouchStart = (e: TouchEvent) => {
      // Ignore touches on buttons or interactive elements
      const target = e.target as HTMLElement;
      if (target.closest('button') || target.closest('.navigation-menu') || target.closest('.settings-panel')) {
        return;
      }
      
      touchStartPos = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
      touchStartTime = Date.now();
      
      // Prevent long-press context menu by setting a timer
      longPressTimer = setTimeout(() => {
        e.preventDefault();
      }, 500);
    };

    const handleTouchEnd = (e: TouchEvent) => {
      // Clear long-press timer
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }
      
      if (!touchStartPos) return;
      
      // Get the target element
      const target = e.target as HTMLElement;
      
      // If tapping on buttons/menus, handle closing panels
      if (target.closest('button') || target.closest('.navigation-menu') || target.closest('.settings-panel')) {
        touchStartPos = null;
        return;
      }
      
      // Only toggle controls on tap outside (not on menus/buttons)
      // Removed menu closing on tap outside to prevent accidental closes
      
      const touch = e.changedTouches[0];
      const deltaX = Math.abs(touch.clientX - touchStartPos.x);
      const deltaY = Math.abs(touch.clientY - touchStartPos.y);
      const touchDuration = Date.now() - touchStartTime;
      
      // Only toggle if it's a quick tap with minimal movement (not a pan/swipe)
      // Max 300ms duration and less than 10px movement
      if (deltaX < TAP_THRESHOLD && deltaY < TAP_THRESHOLD && touchDuration < 300) {
        lastTouchTime = Date.now();
      }
      
      touchStartPos = null;
    };

    const handleTouchCancel = (e: TouchEvent) => {
      // Clear long-press timer and reset state
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }
      touchStartPos = null;
    };

    const handleContextMenu = (e: MouseEvent) => {
      // Disable context menu on touch (long-press)
      const target = e.target as HTMLElement;
      if (!target.closest('input') && !target.closest('textarea')) {
        e.preventDefault();
      }
    };

    const handleClick = (e: MouseEvent) => {
      // Ignore clicks that come right after touch events (prevent double-toggle)
      if (Date.now() - lastTouchTime < 500) {
        return;
      }
      
      // For desktop: do nothing (controls handled by MobileControlsOverlay)
      const target = e.target as HTMLElement;
      if (target.closest('button') || target.closest('.navigation-menu') || target.closest('.settings-panel')) {
        return;
      }
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: false }); // Non-passive to allow preventDefault
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('touchcancel', handleTouchCancel, { passive: true });
    window.addEventListener('contextmenu', handleContextMenu, { passive: false });
    window.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchCancel);
      window.removeEventListener('contextmenu', handleContextMenu);
      window.removeEventListener('click', handleClick);
      
      if (longPressTimer) {
        clearTimeout(longPressTimer);
      }
    };
  }, []);
  
  // Global tap-out handler to dismiss overlays (hints are always visible - decoupled)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as EventTarget;
      
      // Exclude these selectors from triggering dismiss
      const excludeSelectors = [
        'button[data-hint-direction]',  // Navigation hint buttons (always visible, not dismissed)
        '[data-balloon]',                // Frame balloons
        '[data-settings-panel]',         // Settings panel
        '.mobile-controls-overlay',      // Mobile controls
        '[data-tap-indicator]',          // Tap camera indicator circle
        'svg',                           // SVG elements (trajectory, canvas)
      ];
      
      // Check if click should dismiss overlays
      if (shouldDismissOverlays(target, excludeSelectors)) {
        // Simply dismiss all overlays (hints remain always visible)
        useAppStore.getState().dismissAllOverlays();
      }
    };

    window.addEventListener('click', handleClickOutside);

    return () => {
      window.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Sync store overlay visibility flags to component state
  const navigationSettingsBalloonVisible = useAppStore((state) => state.navigationSettingsBalloonVisible);
  useEffect(() => {
    setIsBalloonOpen(navigationSettingsBalloonVisible);
  }, [navigationSettingsBalloonVisible]);

  
  // Show loading state only while we have NO cameras AND we're NOT ready for taps
  // IMPORTANT: Never early return - always render children to avoid breaking hooks order
  const showLoadingOverlay = cameras.length === 0 || (isLoadingData && !canUseTapIndicator)
  
  if (dataError) {
    return (
      <div className="gesture-viewer">
        <div className="gesture-viewer__error">
          <h2>Error loading data</h2>
          <p>{dataError}</p>
          <button onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </div>
    )
  }
  
  if (!currentCamera) {
    // Get stored shop preview image from sessionStorage (set by ShopSelector)
    const shopPreviewImage = typeof window !== 'undefined' ? sessionStorage.getItem(`shop-preview-${shopId}`) : null
    
    // Show shop preview image while loading cameras
    if (shopPreviewImage) {
      console.log(`📸 [GestureViewer] Displaying preview image while loading cameras for ${shopId}`)
      return (
        <div className="gesture-viewer">
          <div className="gesture-viewer__loading-image">
            <img 
              src={shopPreviewImage} 
              alt="Shop preview"
              onError={(e) => {
                console.warn(`⚠️ [GestureViewer] Preview image failed to load: ${shopPreviewImage}`)
                e.currentTarget.style.display = 'none'
              }}
              onLoad={() => {
                console.log(`✅ [GestureViewer] Preview image loaded successfully`)
              }}
            />
            <div className="gesture-viewer__loading-overlay">
              <button 
                className="gesture-viewer__cancel-btn"
                onClick={() => window.location.href = '/'}
                title="Cancel and go back to shop list"
              >
                ✕
              </button>
              <div className="spinner"></div>
              <p>Loading cameras...</p>
            </div>
          </div>
        </div>
      )
    }
    
    // Fallback if no preview image available
    console.warn(`⚠️ [GestureViewer] No preview image found for ${shopId}`)
    return (
      <div className="gesture-viewer">
        <div className="gesture-viewer__error">
          <p>Loading camera data...</p>
        </div>
      </div>
    )
  }
  
  return (
    <div className="gesture-viewer">
      {/* Loading overlay - show only during initial load */}
      {showLoadingOverlay && (
        <div className="gesture-viewer__loading">
          <button 
            className="gesture-viewer__cancel-btn"
            onClick={() => window.location.href = '/'}
            title="Cancel and go back to shop list"
          >
            ✕
          </button>
          <div className="spinner"></div>
          <p>Loading shop, please wait...</p>
        </div>
      )}

      {/* Sync 3D viewer data to localStorage ONLY when 3D mode is enabled */}
      {is3DMode && <ThreeDViewerDataSync />}
      
      {/* Mesh Viewer - Fullscreen 3D mesh display */}
      {showMeshViewer && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1000,
          background: '#1a1a1a'
        }}>
          <MeshViewer />
        </div>
      )}
      
      {/* Always use NewModeViewer */}
      <NewModeViewer 
        shopId={currentDatasetId || shopId}
        showNavigationSettingsBalloon={isBalloonOpen}
        setShowNavigationSettingsBalloon={setIsBalloonOpen}
      />
      
      {/* Unified mobile controls overlay (back, menu, fullscreen) */}
      <MobileControlsOverlay
        onBack={handleBackClick}
        onMenuClick={handleMenuClick}
        isMenuOpen={isMenuOpen}
        isFullscreen={isFullscreen}
        isFullscreenSupported={isFullscreenSupported}
        onToggleFullscreen={toggleFullscreen}
      />
      
      {/* Mobile navigation history buttons */}
      <MobileHistoryButtons />
      
      {/* Navigation menu */}
      <NavigationMenu 
        isOpen={isMenuOpen}
        onClose={handleMenuClose}
        onOpenSettings={handleSettingsOpen}
      />
      
      {/* Settings panel */}
      <SettingsPanel 
        isOpen={isSettingsOpen}
        onClose={handleSettingsClose}
      />
      
      {/* World Space Debug Visualization */}
      {showWorldDebug && <WorldSpaceDebug />}
      
      {/* Sections Drawer - slides up from bottom (only render when stage 2 complete AND sections loaded) */}
      {canUseTapIndicator && sections.length > 0 && <SectionsDrawer />}
    </div>
  )
}

export default GestureViewer
